/**
 * 유튜브 URL 에서 11자리 videoId 를 뽑는다.
 *
 * `youtu.be/<id>`, `watch?v=<id>`, `embed|shorts|live|v/<id>` 형태와
 * id 문자열 자체를 받는다.
 */
export function extractYoutubeId(input: string | undefined | null): string | null {
  if (!input) return null;

  const value = input.trim();
  const isId = (s: string) => /^[A-Za-z0-9_-]{11}$/.test(s);

  if (isId(value)) return value;

  let url: URL;
  try {
    url = new URL(value.startsWith('http') ? value : `https://${value}`);
  } catch {
    return null;
  }

  const host = url.hostname.replace(/^www\./, '');
  const segments = url.pathname.split('/').filter(Boolean);

  if (host === 'youtu.be') {
    return isId(segments[0] ?? '') ? segments[0] : null;
  }

  const isYoutube =
    host === 'youtube.com' || host === 'm.youtube.com' || host.endsWith('.youtube.com');
  if (!isYoutube) return null;

  const v = url.searchParams.get('v');
  if (v && isId(v)) return v;

  if (segments.length >= 2 && ['embed', 'shorts', 'live', 'v'].includes(segments[0])) {
    return isId(segments[1]) ? segments[1] : null;
  }

  return segments.find(isId) ?? null;
}

/** 썸네일 주소 (플레이어 로드 전 표시용) */
export function youtubeThumbnail(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

/** 유튜브 onError 코드별 사유 */
export const YOUTUBE_ERROR: Record<number, string> = {
  2: '잘못된 파라미터입니다. 영상 주소를 확인해 주세요.',
  5: 'HTML5 플레이어 오류입니다.',
  100: '동영상을 찾을 수 없거나 비공개·삭제된 영상입니다.',
  101: '이 동영상은 외부 사이트에서 재생할 수 없습니다.',
  150: '이 동영상은 외부 사이트에서 재생할 수 없습니다.',
};
